import { create } from 'zustand';
import { getHubLogs } from '../services/api';
import type { LogEntry } from '../types/logs';

interface LogsStore {
  // Log entries for different sources
  sysLogs: LogEntry[];
  raspiLogs: LogEntry[];
  
  // Pagination and loading
  currentPage: number;
  entriesPerPage: number;
  hasMorePages: boolean;
  isLoading: boolean;
  
  // Filtering
  selectedRaspiId: string | null;
  selectedLevel: string | null;
  searchTerm: string;
  
  // Sorting
  sortBy: 'timestamp' | 'origin' | 'level' | 'source_component' | 'message';
  sortOrder: 'asc' | 'desc';
  
  // Actions
  setSelectedRaspiId: (raspiId: string | null) => void;
  setSelectedLevel: (level: string | null) => void;
  setSearchTerm: (term: string) => void;
  setSorting: (sortBy: 'timestamp' | 'origin' | 'level' | 'source_component' | 'message', sortOrder: 'asc' | 'desc') => void;
  
  // Data fetching
  fetchLogsPage: (page: number) => Promise<void>;
  addLogEntries: (entries: LogEntry[], source: 'sys' | 'raspi') => void;
  clearLogs: (source: 'sys' | 'raspi') => void;
  
  // Computed properties
  getFilteredLogs: (source: 'sys' | 'raspi') => LogEntry[];
  getSortedLogs: (source: 'sys' | 'raspi') => LogEntry[];
}

export const useLogsStore = create<LogsStore>((set, get) => ({
  // Initial state
  sysLogs: [],
  raspiLogs: [],
  currentPage: 1,
  entriesPerPage: 100,
  hasMorePages: true,
  isLoading: false,
  selectedRaspiId: null,
  selectedLevel: null,
  searchTerm: '',
  sortBy: 'timestamp',
  sortOrder: 'desc',

  // Actions
  setSelectedRaspiId: (raspiId) => set({ selectedRaspiId: raspiId }),
  setSelectedLevel: (level) => set({ selectedLevel: level }),
  setSearchTerm: (term) => set({ searchTerm: term }),
  setSorting: (sortBy, sortOrder) => set({ sortBy, sortOrder }),

  // Data fetching
  fetchLogsPage: async (page) => {
    set({ isLoading: true });
    try {
      const response = await getHubLogs(get().entriesPerPage);
      if (!response) {
        throw new Error('Failed to fetch logs');
      }
      const logs = response.logs || [];
      const hasMore = response.count > page * get().entriesPerPage;
      
      // Parse logs into new format
      const parsedLogs: LogEntry[] = logs.map((log: any) => {
        // If we have a raw_line, try to parse the unified format
        if (log.raw_line && typeof log.raw_line === 'string') {
          const match = log.raw_line.match(/\[([^\]]+)\]\s+\[([^\]]+)\]\s+\[([^\]]+)\]\s+\[([^\]]+)\]\s+\[(.+)\]/);
          if (match) {
            return {
              timestamp: match[1],
              origin: match[2],
              level: match[3],
              source_component: match[4],
              message: match[5],
              raw_line: log.raw_line
            };
          }
        }
        
        // If no raw_line or parsing failed, construct from object properties
        return {
          timestamp: log.timestamp || new Date().toISOString(),
          origin: 'SYS', // Hub logs are always system logs
          level: log.level || 'INFO',
          source_component: log.source || 'HUB',
          message: log.message || 'Unknown log entry',
          raw_line: log.raw_line || `[${log.timestamp}] [SYS] [${log.level}] [${log.source}] [${log.message}]`
        };
      });

      set((state) => ({
        sysLogs: page === 1 ? parsedLogs : [...state.sysLogs, ...parsedLogs],
        currentPage: page,
        hasMorePages: hasMore,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error fetching logs:', error);
      set({ isLoading: false });
    }
  },

  addLogEntries: (entries, source) => {
    set((state) => {
      if (source === 'sys') {
        return {
          ...state,  // Preserve all existing state
          sysLogs: [...entries, ...state.sysLogs].slice(0, 1000) // Keep only last 1000 entries
        };
      } else {
        return {
          ...state,  // Preserve all existing state
          raspiLogs: [...entries, ...state.raspiLogs].slice(0, 1000) // Keep only last 1000 entries
        };
      }
    });
  },

  clearLogs: (source) => {
    if (source === 'sys') {
      set({ sysLogs: [] });
    } else {
      set({ raspiLogs: [] });
    }
  },

  // Computed properties
  getFilteredLogs: (source) => {
    const state = get();
    const logs = source === 'sys' ? state.sysLogs : state.raspiLogs;
    
    return logs.filter(log => {
      // RASPI filter only applies to system logs (hub frame)
      // RASPI frame shows all RASPI logs regardless of selectedRaspiId
      if (source === 'sys' && state.selectedRaspiId && log.origin !== state.selectedRaspiId) {
        return false;
      }
      
      // Filter by level
      if (state.selectedLevel && log.level !== state.selectedLevel) {
        return false;
      }
      
      // Filter by search term
      if (state.searchTerm) {
        const searchLower = state.searchTerm.toLowerCase();
        return (
          log.message.toLowerCase().includes(searchLower) ||
          log.source_component.toLowerCase().includes(searchLower) ||
          log.origin.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
  },

  getSortedLogs: (source) => {
    const state = get();
    const filteredLogs = state.getFilteredLogs(source);
    
    return filteredLogs.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (state.sortBy) {
        case 'timestamp':
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
          break;
        case 'origin':
          aValue = a.origin;
          bValue = b.origin;
          break;
        case 'level':
          aValue = a.level;
          bValue = b.level;
          break;
        case 'source_component':
          aValue = a.source_component;
          bValue = b.source_component;
          break;
        case 'message':
          aValue = a.message;
          bValue = b.message;
          break;
        default:
          aValue = a.timestamp;
          bValue = b.timestamp;
      }
      
      if (state.sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }
})); 